import React, { useState } from 'react';
import DragAndDropListData, { createFakeDataItem } from './drag-and-drop-lists-page.data';
import Button from '@components-clean/core/button';
import Card from '@components-clean/core/card';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import Dialog from '@components-clean/core/dialog';
import DragDropListsCore from '@components-clean/core/drag-drop-lists-core';
import DragDropItem from '@components-clean/core/drag-drop-lists-core/drag-drop-item/index';
import {
    DragDropItemProps,
    DragDropListItemProps,
} from '@components-clean/core/drag-drop-lists-core/drag-drop-lists-core.interface';
import { NumericTextBox } from '@components-clean/core/text-box-numeric';

interface DragAndDropListsPageProps {
    isDisabled: boolean;
}

const DragAndDropListsPage = ({ isDisabled }: DragAndDropListsPageProps) => {
    const [editItem, setEditItem] = useState<DragDropListItemProps | null>(null);

    const [isLeftFilterDisabled, setIsLeftFilterDisabled] = useState(true);
    const [isRightFilterDisabled, setIsRightFilterDisabled] = useState(false);
    const [isTransferAllDisabled, setIsTransferAllDisabled] = useState(false);
    const [isReorderDisabled, setIsReorderDisabled] = useState(false);

    const [disableEditLeft, setDisableEditLeft] = useState(false);
    const [disableEditRight, setDisableEditRight] = useState(false);

    const [leftItems, setLeftItems] = useState(DragAndDropListData.left);
    const [rightItems, setRightItems] = useState(DragAndDropListData.right);

    const [leftFakeItemCount, setLeftFakeItemCount] = useState(0);
    const [rightFakeItemCount, setRightFakeItemCount] = useState(0);
    const [editButtonDeletes, setEditButtonDeletes] = useState(false);
    const [logItemRerenders, setLogItemRerenders] = useState(false);

    function refreshFakeData() {
        setLeftItems(Array.from(Array(leftFakeItemCount)).map((_, i) => createFakeDataItem(i)));
        setRightItems(Array.from(Array(rightFakeItemCount)).map((_, i) => createFakeDataItem(i)));
    }

    function onLeftEditButtonClicked(item: DragDropItemProps) {
        if (editButtonDeletes) {
            const index = leftItems.findIndex((i) => i.panelId === item.dataItem.panelId);
            if (index === -1) {
                return;
            }
            setLeftItems((x) => x.toSpliced(index, 1));
        } else {
            setEditItem(item.dataItem);
        }
    }
    function onRightEditButtonClicked(item: DragDropItemProps) {
        if (editButtonDeletes) {
            const index = rightItems.findIndex((i) => i.panelId === item.dataItem.panelId);
            if (index === -1) {
                return;
            }
            setRightItems((x) => x.toSpliced(index, 1));
        } else {
            setEditItem(item.dataItem);
        }
    }

    const cardBodyStyles = {
        display: 'grid',
        gap: '6px',
    };

    const cardRowStyles = {
        display: 'grid',
        gap: '6px',
        gridTemplateColumns: 'repeat(2, 1fr)',
    };

    return (
        <div
            style={{
                display: 'grid',
                gridTemplateRows: 'auto 1fr',
                gap: 10,
                height: '100%',
            }}
        >
            {!!editItem && (
                <Dialog title="Edit Item" data-testid="edit-item-dialog" closeIcon onClose={() => setEditItem(null)}>
                    <>{editItem.title}</>
                </Dialog>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
                <Card title="Data" data-testid="data-card" bodyStyles={cardBodyStyles}>
                    <div style={cardRowStyles}>
                        <NumericTextBox
                            data-testid="fake-data-left-count"
                            min={0}
                            max={10000}
                            label="Left Item Count"
                            value={leftFakeItemCount}
                            onChange={(e) => setLeftFakeItemCount(e.target.value ?? 0)}
                        />
                        <NumericTextBox
                            data-testid="fake-data-right-count"
                            min={0}
                            max={10000}
                            label="Right Item Count"
                            value={rightFakeItemCount}
                            onChange={(e) => setRightFakeItemCount(e.target.value ?? 0)}
                        />
                    </div>

                    <Button data-testid="fake-data-submit-button" onClick={() => refreshFakeData()}>
                        Apply
                    </Button>
                </Card>
                <Card title="Filters" data-testid="filters-card" bodyStyles={cardBodyStyles}>
                    <div style={cardRowStyles}>
                        <CoreCheckbox
                            label="Disable Left"
                            data-testid="is-left-filter-disabled-toggle"
                            defaultChecked={isLeftFilterDisabled}
                            onChange={(e) => {
                                setIsLeftFilterDisabled(e.value);
                            }}
                        />

                        <CoreCheckbox
                            label="Disable Right"
                            data-testid="is-right-filter-disabled-toggle"
                            defaultChecked={isRightFilterDisabled}
                            onChange={(e) => {
                                setIsRightFilterDisabled(e.value);
                            }}
                        />
                    </div>
                </Card>

                <Card title="Actions" data-testid="actions-card" bodyStyles={cardBodyStyles}>
                    <div style={cardRowStyles}>
                        <CoreCheckbox
                            label="Hide Transfer All"
                            data-testid="is-hiding-transfer-all-toggle"
                            defaultChecked={isTransferAllDisabled}
                            onChange={(e) => {
                                setIsTransferAllDisabled(e.value);
                            }}
                        />

                        <CoreCheckbox
                            label="Disable Reorder"
                            data-testid="is-reorder-disabled-toggle"
                            defaultChecked={isReorderDisabled}
                            onChange={(e) => {
                                setIsReorderDisabled(e.value);
                            }}
                        />
                    </div>
                </Card>

                <Card title="Editing" data-testid="editing" bodyStyles={cardBodyStyles}>
                    <div style={cardRowStyles}>
                        <CoreCheckbox
                            label="Disable edit left"
                            data-testid="disable-edit-left"
                            defaultChecked={disableEditLeft}
                            onChange={(e) => {
                                setDisableEditLeft(e.value);
                            }}
                        />
                        <CoreCheckbox
                            label="Disable edit right"
                            data-testid="disable-edit-right"
                            defaultChecked={disableEditRight}
                            onChange={(e) => {
                                setDisableEditRight(e.value);
                            }}
                        />
                    </div>

                    <div style={cardRowStyles}>
                        <CoreCheckbox
                            label="Edit button Deletes"
                            data-testid="disable-edit-right"
                            defaultChecked={editButtonDeletes}
                            onChange={(e) => {
                                setEditButtonDeletes(e.value);
                            }}
                        />
                        <CoreCheckbox
                            label="Print Item Rerenders to Console"
                            data-testid="disable-edit-right"
                            defaultChecked={logItemRerenders}
                            onChange={(e) => {
                                setLogItemRerenders(e.value);
                            }}
                        />
                    </div>
                </Card>
            </div>

            <Card data-testid="drag-drop-lists-card">
                <DragDropListsCore
                    list1Header="Left Items"
                    list2Header="Right Items"
                    list1Items={leftItems}
                    list2Items={rightItems}
                    idField="panelId"
                    onChangeItemsLeft={(leftItemsAfterChange) => {
                        setLeftItems(leftItemsAfterChange);
                    }}
                    onChangeItemsRight={(rightItemsAfterChange) => {
                        setRightItems(rightItemsAfterChange);
                    }}
                    isTwoLists
                    disabled={isDisabled}
                    renderItemLeft={(item) => {
                        if (logItemRerenders) {
                            // eslint-disable-next-line no-console
                            console.log('Left item rerender');
                        }
                        return (
                            <DragDropItem
                                {...item}
                                editButtonClick={disableEditLeft ? undefined : () => onLeftEditButtonClicked(item)}
                            />
                        );
                    }}
                    renderItemRight={(item) => {
                        if (logItemRerenders) {
                            // eslint-disable-next-line no-console
                            console.log('Right item rerender');
                        }
                        return (
                            <DragDropItem
                                {...item}
                                editButtonClick={disableEditRight ? undefined : () => onRightEditButtonClicked(item)}
                            />
                        );
                    }}
                    disableFilterLeft={isLeftFilterDisabled}
                    disableFilterRight={isRightFilterDisabled}
                    hideTransferAllButtons={isTransferAllDisabled}
                    disableReorder={isReorderDisabled}
                />
            </Card>
        </div>
    );
};

export default DragAndDropListsPage;
